<template>
  <div class="news-tag">
    <!-- 页头：返回 + 标签名 + 相关的文章 -->
    <div class="news-tag-head">
      <div class="news-tag-back" @click="goBack">
        <svg width="8" height="15" viewBox="0 0 8 15" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M7 1L1 7.5L7 14" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        返回
      </div>
      <div class="news-tag-title">
        <span class="news-tag-name">{{ tagName || '标签' }}</span>
        <span class="news-tag-sub">相关的文章</span>
      </div>
    </div>

    <!-- 主体 -->
    <div class="news-tag-main">
      <!-- 加载中 -->
      <div v-if="loading" class="news-tag-loading">加载中...</div>

      <!-- 文章列表 -->
      <div v-else-if="list.length" class="news-tag-grid">
        <div
          v-for="post in list"
          :key="post.id"
          class="news-tag-card"
          @click="goDetail(post)"
        >
          <!-- 封面 -->
          <div class="news-tag-cover">
            <img v-if="post.cover" :src="post.cover" :alt="post.title" loading="lazy" />
            <div v-else class="news-tag-cover-placeholder">节点小宝</div>
          </div>
          <!-- 文字区 -->
          <div class="news-tag-body">
            <div class="news-tag-card-title">{{ post.title }}</div>
            <div class="news-tag-excerpt">{{ post.excerpt }}</div>
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-else class="news-tag-empty">该标签下暂无文章</div>

      <!-- 分页 -->
      <div v-if="totalPages > 1" class="news-tag-pager">
        <div class="news-tag-page-arrow" :class="{ 'news-tag-page-arrow-disabled': page <= 1 }" @click="goPage(page - 1)">
          <svg width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 1L2 8L9 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div class="news-tag-pages">
          <div
            v-for="(pageItem, i) in pageItems"
            :key="i"
            class="news-tag-page-num"
            :class="{ 'news-tag-page-num-active': pageItem === page, 'news-tag-page-more': pageItem === '...' }"
            @click="clickPage(pageItem, i)"
          >
            <!-- 省略号：默认三点图标，悬浮时切换为双箭头（与 element 分页一致） -->
            <template v-if="pageItem === '...'">
              <svg class="more-dots" width="16" height="4" viewBox="0 0 16 4" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="2" cy="2" r="2" fill="currentColor"/>
                <circle cx="8" cy="2" r="2" fill="currentColor"/>
                <circle cx="14" cy="2" r="2" fill="currentColor"/>
              </svg>
              <!-- 双箭头基准为左向 «，右省略号通过镜像翻转成右向 »（element 分页交互） -->
              <svg class="more-arrows" :class="{ 'more-arrows-flip': !isLeftMore(i) }" width="16" height="14" viewBox="0 0 16 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 1L1 7L7 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M15 1L9 7L15 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </template>
            <template v-else>{{ pageItem }}</template>
          </div>
        </div>
        <div class="news-tag-page-arrow" :class="{ 'news-tag-page-arrow-disabled': page >= totalPages }" @click="goPage(page + 1)">
          <svg width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1L8 8L1 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
      </div>
    </div>

    <Foot></Foot>
  </div>
</template>

<script>
import { getPosts, getTag } from "../req/wp";
import Foot from "@/components/Foot.vue";

export default {
  name: "NewsTag",
  components: {
    Foot,
  },
  data: function () {
    return {
      tagId: "",
      tagName: "",
      list: [],
      page: 1,
      totalPages: 1,
      loading: false,
    };
  },
  computed: {
    // 页码窗口：生成分页条上要显示的数字序列，省略号代表中间被折叠的页码。
    // 策略：总页数少时全部显示；页数多时只保留 [首页, 当前页前后各1页, 尾页]，
    //       其余页码折叠成省略号。
    pageItems: function () {
      // ---------- 窗口策略参数 ----------
      // 总页数不超过该值时全部显示，不做折叠
      const MAX_VISIBLE_PAGES = 7;
      // 当前页两侧各连续保留的页码数（1 表示前后各显示一页）
      const NEIGHBOR_COUNT = 1;

      const total = this.totalPages;
      const cur = this.page;
      const pages = [];

      // 总页数少，全部显示
      if (total <= MAX_VISIBLE_PAGES) {
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
        return pages;
      }

      // 首页固定显示
      pages.push(1);

      // 左侧省略号条件：首页与当前页连续区之间真的藏了页码才显示，
      // 即 当前页左侧可隐藏的页数 >= 1
      if (cur - NEIGHBOR_COUNT >= 3) {
        pages.push("...");
      }

      // 当前页附近连续显示的页码范围：
      //   左边界：当前页往前 NEIGHBOR_COUNT 页，但不低于第 2 页（第 1 页已单独显示）
      //   右边界：当前页往后 NEIGHBOR_COUNT 页，但不超过倒数第 2 页（尾页已单独显示）
      const from = Math.max(2, cur - NEIGHBOR_COUNT);
      const to = Math.min(total - 1, cur + NEIGHBOR_COUNT);
      for (let i = from; i <= to; i++) {
        pages.push(i);
      }

      // 右侧省略号条件：与左侧对称，当前页右侧可隐藏的页数 >= 1
      if (total - cur - NEIGHBOR_COUNT >= 2) {
        pages.push("...");
      }

      // 尾页固定显示
      pages.push(total);

      return pages;
    },
  },
  created: function () {
    this.tagId = this.$route.params.tagId;
    this.loadTagName();
    this.loadPosts();
  },
  watch: {
    // keep-alive 缓存组件时 created 不会再次触发；
    // 只在路由仍是本页（带新 tagId）时重新加载，离开本页（如点"返回"）则忽略，避免请求 undefined
    $route: function () {
      const tagId = this.$route.params.tagId;
      if (!tagId) {
        return;
      }
      if (tagId === this.tagId) {
        return;
      }
      this.tagId = tagId;
      this.page = 1;
      this.loadTagName();
      this.loadPosts();
    },
  },
  methods: {
    // 加载标签名，用于标题展示与页签标题
    loadTagName: function () {
      getTag(this.tagId)
        .then((tag) => {
          this.tagName = tag.name;
          document.title = tag.name + " - 节点小宝资讯";
        })
        .catch((err) => {
          console.error("标签信息加载失败", err);
          this.tagName = "";
        });
    },
    // 加载该标签下的文章列表
    loadPosts: function () {
      this.loading = true;
      getPosts({ page: this.page, tag: this.tagId })
        .then((res) => {
          this.list = res.list;
          this.totalPages = res.totalPages;
        })
        .catch((err) => {
          console.error("标签文章列表加载失败", err);
          this.list = [];
          this.totalPages = 1;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    // 点击页码：省略号与 element 分页一致，快进/快退一屏
    clickPage: function (page, index) {
      // 省略号跳转步数：与 element-ui pager 一致（pagerCount 7 - 2 = 5）
      const MORE_OFFSET = 5;
      if (page === "...") {
        // element 行为：越界时到达首尾页（而非不跳），既未越界则正常跳转
        const currentIndex = this.pageItems.indexOf(this.page);
        let target = index < currentIndex ? this.page - MORE_OFFSET : this.page + MORE_OFFSET;
        if (target < 1) {
          target = 1;
        }
        if (target > this.totalPages) {
          target = this.totalPages;
        }
        if (target !== this.page) {
          this.goPage(target);
        }
        return;
      }
      this.goPage(page);
    },
    // 判断省略号是否位于当前页左侧（用于 hover 双箭头方向）
    isLeftMore: function (index) {
      const currentIndex = this.pageItems.indexOf(this.page);
      return index < currentIndex;
    },
    // 跳转指定页：越界或重复点击时忽略，翻页后回到页面顶部
    goPage: function (page) {
      if (page < 1 || page > this.totalPages || page === this.page) {
        return;
      }
      this.page = page;
      this.loadPosts();
      window.scrollTo(0, 0);
    },
    // 返回：优先回退到上一页（通常是来源详情页），无记录时回资讯列表
    goBack: function () {
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        this.$router.push("/news");
      }
    },
    // 进入文章详情页
    goDetail: function (post) {
      this.$router.push("/news/detail/" + post.id);
    },
  },
};
</script>

<!-- 页面背景 #F7F9FE，页头是返回 + 标签名 + 相关的文章，卡片与资讯首页一致 -->
<style scoped lang="less">
.news-tag {
  min-height: 600px;
  background: #f7f9fe;

  /* ---------- 页头 ---------- */
  .news-tag-head {
    width: 1280px;
    margin: 0 auto;
    padding: 40px 0 12px;
    .news-tag-back {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-size: 20px;
      color: #1d1f24;
      cursor: pointer;
      transition: color 0.2s;
      &:hover {
        color: #3772ff;
      }
    }
    .news-tag-title {
      margin-top: 16px;
      display: flex;
      align-items: baseline;
      gap: 10px;
      .news-tag-name {
        font-size: 20px;
        font-weight: 700;
        color: #3772ff;
      }
      .news-tag-sub {
        font-size: 20px;
        font-weight: 400;
        color: #1d1f24;
      }
    }
  }

  /* ---------- 主体 ---------- */
  .news-tag-main {
    width: 1280px;
    margin: 0 auto;
    padding-bottom: 80px;
    box-sizing: border-box;
  }

  .news-tag-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 40px;
  }
  .news-tag-card {
    background: #ffffff;
    border-radius: 20px;
    overflow: hidden;
    cursor: pointer;
    transition: transform 0.25s ease, box-shadow 0.25s ease;
    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 24px rgba(23, 33, 94, 0.08);
      .news-tag-card-title {
        color: #3772ff;
      }
    }
    .news-tag-cover {
      position: relative;
      width: 100%;
      padding-top: 55%;
      overflow: hidden;
      background: #f0f2f8;
      img {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
      .news-tag-cover-placeholder {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 30px;
        font-weight: 700;
        color: #ffffff;
        background: linear-gradient(135deg, #2f6bff 0%, #4e4cff 60%, #6a5cff 100%);
      }
    }
    .news-tag-body {
      padding: 23px 18px 20px;
      .news-tag-card-title {
        font-size: 20px;
        font-weight: 500;
        line-height: 30px;
        color: #17171b;
        transition: color 0.2s;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        overflow: hidden;
      }
      .news-tag-excerpt {
        margin-top: 10px;
        font-size: 14px;
        font-weight: 350;
        line-height: 22px;
        color: #57576c;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        overflow: hidden;
      }
    }
  }

  /* ---------- 加载 / 空状态 ---------- */
  .news-tag-loading {
    padding: 80px 0;
    text-align: center;
    font-size: 16px;
    color: #999da6;
  }
  .news-tag-empty {
    padding: 100px 0;
    text-align: center;
    font-size: 16px;
    color: #999da6;
  }

  /* ---------- 分页（与资讯首页一致） ---------- */
  .news-tag-pager {
    margin-top: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    .news-tag-page-arrow {
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #252a3a;
      cursor: pointer;
      transition: color 0.2s;
      &:hover {
        color: #3772ff;
      }
      &.news-tag-page-arrow-disabled {
        color: #aeb2be;
        cursor: not-allowed;
        &:hover {
          color: #aeb2be;
        }
      }
    }
    .news-tag-pages {
      display: flex;
      gap: 10px;
    }
    .news-tag-page-num {
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      font-weight: 400;
      color: #252a3a;
      background: #ffffff;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s;
      &:hover {
        color: #3772ff;
      }
      &.news-tag-page-num-active {
        color: #ffffff;
        background: #3772ff;
      }

      // 省略号：默认三点图标垂直居中，悬浮时显示双箭头（element 分页交互）
      &.news-tag-page-more {
        .more-arrows {
          display: none;
        }
        // 右侧省略号 hover 时镜像成右向双箭头 »
        .more-arrows-flip {
          transform: scaleX(-1);
        }
        &:hover {
          .more-dots {
            display: none;
          }
          .more-arrows {
            display: block;
          }
        }
      }
    }
  }
}
</style>
